import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';
import { BaseRespone, PaginationResult } from 'src/app/models/common.response';
import { environment } from 'src/environments/environment';
import { Iipd, IoperationSummary } from './Iipd.type';
import { IActiveIPDPatient, IBedOccupancyResult } from './ipd-management/iipd-management';

@Injectable({
  providedIn: 'root'
})
export class IpdService {

  private _ipd: BehaviorSubject<Iipd | null> = new BehaviorSubject<Iipd | null>(null);
  private _operationSummary: BehaviorSubject<IoperationSummary | null> = new BehaviorSubject<IoperationSummary | null>(null);
  private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject<PaginationResult | null>(null);
  private _ipds: BehaviorSubject<Iipd[] | null> = new BehaviorSubject<Iipd[] | null>(null);


  constructor(private _httpClient: HttpClient) { }

  get ipd$(): Observable<Iipd | null> {
    return this._ipd.asObservable();
  }

  get pagination$(): Observable<PaginationResult | null> {
    return this._pagination.asObservable();
  }

  get ipds$(): Observable<Iipd[] | null> {
    return this._ipds.asObservable();
  }


  createIPD(ipd: Iipd): Observable<BaseRespone<Iipd>> {
    return this._httpClient.post<BaseRespone<Iipd>>(`${environment.baseURl}/IPD`, ipd).pipe(
      tap((response) => {
        this._ipd.next(response.result);
      }),
      catchError((error) => {
        console.error('Error creating IPD:', error);
        throw error;
      })
    );
  }

  getIPDById(ipdId: number): Observable<BaseRespone<Iipd>> {
    return this._httpClient.get<BaseRespone<any>>(`${environment.baseURl}/IPD/${ipdId}`).pipe(
      tap((response) => {
        this._ipd.next(response.result);
      }),
      catchError((error) => {
        return throwError(() => error.error);
      })
    );
  }

  updateIPD(ipd: Iipd): Observable<BaseRespone<Iipd>> {
    return this._httpClient.put<BaseRespone<Iipd>>(`${environment.baseURl}/IPD`, ipd).pipe(
      tap((response) => {
        this._ipd.next(response.result);
      }),
      catchError((error) => {
        console.error('Error updating IPD:', error);
        throw error;
      })
    );
  }

  createOperationSummary(operation: IoperationSummary): Observable<BaseRespone<IoperationSummary>> {
    return this._httpClient.post<BaseRespone<IoperationSummary>>(`${environment.baseURl}/OperationSummary`, operation).pipe(
      tap((response) => {
        this._operationSummary.next(response.result);
      }),
      catchError((error) => {
        console.error('Error creating Operation Summary:', error);
        throw error;
      })
    );
  }

  getOperationSummaryByIpdId(ipdId: number): Observable<BaseRespone<IoperationSummary>> {
    return this._httpClient.get<BaseRespone<IoperationSummary>>(`${environment.baseURl}/OperationSummary/ipd/${ipdId}`).pipe(
      tap((response) => {
        if (response.result) {
          this._operationSummary.next(response.result);
        }
      }),
      catchError((error) => {
        console.error('Error fetching Operation Summary by IPD ID:', error);
        return throwError(() => error.error);
      })
    );
  }

  updateOperationSummary(operation: IoperationSummary): Observable<BaseRespone<IoperationSummary>> {
    return this._httpClient.put<BaseRespone<IoperationSummary>>(`${environment.baseURl}/OperationSummary`, operation).pipe(
      tap((response) => {
        this._operationSummary.next(response.result);
      }),
      catchError((error) => {
        console.error('Error updating Operation Summary:', error);
        throw error;
      })
    );
  }

  getActiveIPDPatients(request: { wardId: number | null; doctorIds: number[] | null; searchText: string | null }): Observable<BaseRespone<IActiveIPDPatient[]>> {
    return this._httpClient.post<BaseRespone<IActiveIPDPatient[]>>(
      `${environment.baseURl}/IPD/active`, request
    );
  }

  getBedOccupancy(): Observable<BaseRespone<IBedOccupancyResult>> {
    return this._httpClient.get<BaseRespone<IBedOccupancyResult>>(
      `${environment.baseURl}/IPD/bed-occupancy`
    );
  }

  dischargeIPD(ipd: Iipd): Observable<BaseRespone<Iipd>> {
    return this._httpClient.put<BaseRespone<Iipd>>(`${environment.baseURl}/IPD/discharge`, ipd).pipe(
      tap((response) => {
        this._ipd.next(response.result);
      }),
      catchError((error) => {
        console.error('Error discharging IPD:', error);
        throw error;
      })
    );
  }
}